import { Box } from '@mantine/core'
import { useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import {
  useCreateAiBridge,
  useInvalidateAiBridgeList
} from '@/queries/aiBridgeQueries'
import { showToast } from '@/theme/notifications'
import { Device } from '@/types/device'
import { LoadingOverlay } from '../ui-shared/LoadingOverlay/LoadingOverlay'
import { AddAiBridgeForm, FormValues } from './AddAiBridgeForm/AddAiBridgeForm'
import { useAiBridgeTest } from './useAiBridgeTest'

type AddAiBridgeHandlerProps = {
  devices: Device[]
  onCancel: () => void
  onAiBridgeCreated: () => void
}

export const AddAiBridgeHandler = ({
  devices,
  onCancel,
  onAiBridgeCreated
}: AddAiBridgeHandlerProps) => {
  const intl = useIntl()

  const { mutateAsync: createAiBridge, isPending } = useCreateAiBridge()
  const { invalidateAiBridgeList } = useInvalidateAiBridgeList()

  const { testPath, isTesting, result, resetResult } = useAiBridgeTest()

  const handleAiBridgeCreate = async (values: FormValues) => {
    try {
      await createAiBridge(values)

      void invalidateAiBridgeList()
      showToast(intl.formatMessage({ id: 'aiBridge.add.success' }), 'success')
      onAiBridgeCreated()
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'aiBridge.add.error' })
        ),
        'error'
      )
    }
  }

  return (
    <Box pos="relative">
      <LoadingOverlay visible={isPending} />

      <AddAiBridgeForm
        devices={devices}
        isTesting={isTesting}
        testResult={result}
        onTest={(values) => void testPath(values)}
        onResetResult={resetResult}
        onCancel={onCancel}
        onSubmit={(values) => void handleAiBridgeCreate(values)}
      />
    </Box>
  )
}
